import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Hamburger from './Hamburger';
import '../styles/Navbar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { useTranslations } from '../hooks/useTranslations';

const Navbar = () => {
  const [showPanels, setShowPanels] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);
  const { t, locale, setLocale } = useTranslations();

  useEffect(() => {
    document.body.style.overflow = showPanels ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showPanels]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    lastScrollYRef.current = window.scrollY;

    const updateVisibility = () => {
      const currentY = window.scrollY;
      const lastY = lastScrollYRef.current;

      if (Math.abs(currentY - lastY) < 12) {
        tickingRef.current = false;
        return;
      }

      if (currentY > lastY && currentY > 120 && !showPanels) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      lastScrollYRef.current = currentY;
      tickingRef.current = false;
    };

    const handleScroll = () => {
      if (!tickingRef.current) {
        tickingRef.current = true;
        window.requestAnimationFrame(updateVisibility);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showPanels]);

  useEffect(() => {
    if (showPanels) {
      setIsVisible(true);
    }
  }, [showPanels]);

  const togglePanels = (isOpen) => {
    setShowPanels(isOpen);
  };

  const handleLinkClick = () => {
    setShowPanels(false);
    window.scrollTo(0, 0);
  };

  const handleLanguageToggle = () => {
    setLocale(locale === 'fi' ? 'en' : 'fi');
  };

  const languageFlag = locale === 'fi' ? '🇬🇧' : '🇫🇮';
  const languageLabel =
    locale === 'fi'
      ? t('navbar.language.switchToEnglish', 'Vaihda englanniksi')
      : t('navbar.language.switchToFinnish', 'Switch to Finnish');

  return (
      <div className="screen">
        <nav className={`navbar ${isVisible ? '' : 'navbar--hidden'}`} aria-label={t('navbar.aria.mainNav', 'Päänavigaatio')}>
          <div className="logo">
            <Link to="/" aria-label={t('navbar.aria.goHome', 'Siirry etusivulle')} onClick={handleLinkClick}>
              <img
                  src="logo192.png"
                  alt={t('navbar.logoAlt', 'TuutoriKeskus logo')}
                  className="tuutorikeskus-logo"
                  loading="lazy"
              />
            </Link>
          </div>
          <div className="nav-actions">
            {/* Navigation Panel */}
            <div className={`nav-links ${showPanels ? 'show' : ''}`} id="primary-navigation">
              <Link to="/" onClick={handleLinkClick}>
                {t('navbar.links.home', 'Koti')}
              </Link>
              <Link to="/Kurssit" onClick={handleLinkClick}>
                {t('navbar.links.courses', 'Kurssit')}
              </Link>
              <Link to="/Romania" onClick={handleLinkClick}>
                {t('navbar.links.romania', 'Romania')}
              </Link>
              <Link to="/Meistä" onClick={handleLinkClick}>
                {t('navbar.links.about', 'Meistä')}
              </Link>
              <Link to="/Yhteistiedot" onClick={handleLinkClick}>
                {t('navbar.links.contact', 'Yhteystiedot')}
              </Link>
            </div>
            <button
              type="button"
              className="language-toggle"
              onClick={handleLanguageToggle}
              aria-label={languageLabel}
            >
              <span aria-hidden="true">{languageFlag}</span>
            </button>
          </div>
          {/* Contact Panel */}
          <div className={`contact-panel ${showPanels ? 'show' : ''}`}>
            <h3>{t('navbar.contact.emailHeading', 'Sähköposti')}</h3>
            <p>
              <a href="mailto:info.tuutorikeskus@gmail.com">
                info.tuutorikeskus@gmail.com
              </a>
            </p>
            <h3>{t('navbar.contact.phoneHeading', 'Puhelin')}</h3>
            <p>
              <a href="tel:+358442447576">
                +358 442 447 576
              </a>
            </p>
            <h3>{t('navbar.contact.socialHeading', 'Sosiaalimediat')}</h3>
            <div className="social-icons">
              <a
                  href="https://www.facebook.com/profile.php?id=100063566469459"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                <FontAwesomeIcon color="#4267B2" icon={faFacebook} aria-label={t('navbar.contact.facebook', 'Facebook')} />
              </a>
              <a
                  href="https://www.instagram.com/tuutorikeskus/"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                <FontAwesomeIcon color="#212121" icon={faInstagram} aria-label={t('navbar.contact.instagram', 'Instagram')} />
              </a>
              <a
                  href="https://www.youtube.com/@TuutoriKeskus"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                <FontAwesomeIcon color="#FF0000" icon={faYoutube} aria-label={t('navbar.contact.youtube', 'YouTube')} />
              </a>
            </div>
          </div>
          <Hamburger onToggle={togglePanels} />
        </nav>
      </div>
  );
};

export default Navbar;
